// src/M_Components/MTabsInpage.jsx
import React, { useState, useContext, useEffect, useMemo } from "react";
import { Tabs, Tab, Box, useMediaQuery } from "@mui/material";
import { LoadingContext } from "../M_Utils";
import PropTypes from "prop-types";

export function MTabsInpage({
  type,
  renderTabs,
  labelColor,
  lineBottomTabs,
  selectedColor,
  firstTabOpen,
  hoverSelectedColor,
  onGetTab,
  // keeps the rail/bar from shrinking in flexible layouts
  fixedArea = true,
  // lateral rail width when type="column"
  railWidth = "300px",
  // top bar height when type="tabs"
  barHeight = "55px",
}) {
  const [value, setValue] = useState(firstTabOpen || 1);
  const ctx = useContext(LoadingContext);

  // Breakpoint used to compact the top MUI tabs on small screens
  const isMobile = useMediaQuery("(max-width:900px)");

  labelColor = labelColor || "var(--color-white)";
  selectedColor = selectedColor || ctx?.secondaryColor || "var(--color-black)";
  hoverSelectedColor =
    hoverSelectedColor || ctx?.tertiaryColor || "var(--color-black)";
  lineBottomTabs =
    lineBottomTabs || ctx?.tertiaryColor || "var(--color-grey-light)";

  const changeTab = (_event, newValue) => setValue(newValue);

  // Keep selected value in sync with external firstTabOpen
  useEffect(() => {
    setValue(firstTabOpen || 1);
  }, [firstTabOpen]);

  // Non-conflicting container sizing; avoid inline borders so page CSS rules win
  const containerStyle = useMemo(() => {
    if (type === "column") {
      return fixedArea
        ? {
            flex: `0 0 ${railWidth}`,
            minWidth: railWidth,
            maxWidth: railWidth,
            overflow: "auto", // allow long lists to scroll internally if needed
          }
        : { width: "100%" };
    }
    if (type === "tabs") {
      return fixedArea
        ? {
            width: "100%",
            minHeight: barHeight,
            position: "sticky",
            top: 0,
            zIndex: 5,
            background: "var(--color-white)",
            flexShrink: 0,
          }
        : { width: "100%" };
    }
    return { width: "100%" };
  }, [type, fixedArea, railWidth, barHeight]);

  // Visual tuning for MUI Tabs based on screen size
  const effectiveBarHeight = isMobile ? 40 : barHeight;
  const tabFontSize = isMobile ? "0.85rem" : "0.95rem";
  const tabPadding = isMobile ? "6px 12px" : "8px 16px";

  // Colors used to mimic the sidebar pill look
  const pillBorderColor = "var(--color-grey)";
  const pillHoverBorder = "var(--color-red-dark)";
  const pillSelectedBg = "var(--color-red-dark)";
  const baseTabColor = "var(--color-black)";

  return (
    <div
      className={`mtip ${
        type === "column" ? "mtip--column" : type === "tabs" ? "mtip--tabs" : ""
      }`}
      style={containerStyle}
    >
      {/* ---------- TOP TABS MODE (MUI Tabs) ---------- */}
      {type === "tabs" && (
        <Box>
          <Tabs
            sx={{
              "& .MuiTabs-root": { width: "100%" },
              // Scroller is the element that actually scrolls horizontally
              "& .MuiTabs-scroller": {
                overflowX: "auto !important",
                "&::-webkit-scrollbar": {
                  height: 8,
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--color-red-dark)",
                  borderRadius: 8,
                },
              },
              "& .MuiTabs-flexContainer": {
                borderBottom: `2px solid ${lineBottomTabs}`,
                minHeight: effectiveBarHeight,
                width: "100%",
                alignItems: "stretch",
              },
              // Base style for every tab, using a pill shape similar to the column sidebar
              "& .MuiTab-root": {
                backgroundColor: "var(--color-white)",
                color: baseTabColor,
                textTransform: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: isMobile ? 500 : "bold",
                minHeight: effectiveBarHeight,
                padding: tabPadding,
                fontSize: tabFontSize,
                maxWidth: "none",
                borderRadius: 8,
                border: `0.5px solid ${pillBorderColor}`,
                marginRight: isMobile ? 4 : 8,
                transition:
                  "transform 0.08s ease, background 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease, color 0.12s ease",
                "&:hover": {
                  backgroundColor: "var(--color-white)",
                  color: pillHoverBorder,
                  borderColor: pillHoverBorder,
                  boxShadow: "0 0 0 3px rgba(208, 0, 0, 0.08)",
                },
                "&:active": {
                  transform: "translateY(1px)",
                },
              },
              // Selected tab mimics the sidebar selected pill (red background + white text)
              "& .MuiTab-root.Mui-selected": {
                backgroundColor: pillSelectedBg,
                color: "var(--color-white)",
                borderColor: pillSelectedBg,
                boxShadow: "0 0 0 3px rgba(208, 0, 0, 0.08)",
              },
              // Indicator is kept but with zero height so it does not change the visual shape
              "& .MuiTabs-indicator": {
                backgroundColor: selectedColor,
                height: 0,
              },
              "& .MuiTab-iconWrapper": { width: "19px" },
              "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 1 },
              "& .MuiTabScrollButton-root": {
                backgroundColor: "var(--color-white) !important",
                color: `${
                  ctx?.secondaryColor || "var(--color-black)"
                } !important`,
                minWidth: isMobile ? 28 : 40,
              },
              "& .MuiTabScrollButton-root.Mui-disabled": {
                backgroundColor: "var(--color-white) !important",
                color: `${
                  ctx?.secondaryColor || "var(--color-black)"
                } !important`,
              },
            }}
            value={value}
            onChange={changeTab}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
          >
            {renderTabs?.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.labelTab}
                icon={tab.iconTab}
                iconPosition="start"
                disableRipple
                tabIndex={0}
                // onGetTab receives the full tab object as first argument in tabs mode
                onClick={() => onGetTab?.(tab, tab)}
                onKeyDown={(event) => {
                  if (event.code === "Enter") {
                    onGetTab?.(tab, tab);
                    setValue(tab.id);
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* ---------- COLUMN RAIL MODE (LEFT) ---------- */}
      {type === "column" && (
        // IMPORTANT: use tabs roles so tabsInpage.css selectors apply
        <div
          role="tablist"
          aria-orientation="vertical"
          className="mtip__list"
          style={{ width: "100%", padding: "5px" }}
        >
          {renderTabs?.map((tab) => {
            const isSelected = tab.id === value;
            return (
              <div
                key={tab.id}
                role="tab"
                aria-selected={isSelected ? "true" : "false"}
                tabIndex={0}
                className="mtip__item"
                // Keep minimal visual fallback; main look comes from CSS
                style={{
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: "10px",
                }}
                onClick={() => {
                  // onGetTab receives the full tab object in column mode
                  onGetTab?.(tab);
                  setValue(tab.id);
                }}
                onKeyDown={(event) => {
                  if (event.code === "Enter") {
                    onGetTab?.(tab);
                    setValue(tab.id);
                  }
                }}
              >
                <div aria-hidden="true">{tab.iconTab}</div>
                <span>{tab.labelTab}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Optional body (compat) */}
      {renderTabs?.map((tab) => (
        <div
          key={tab.id}
          style={{ display: tab.id === value ? "block" : "none" }}
        >
          {tab.renderBody}
        </div>
      ))}
    </div>
  );
}

MTabsInpage.propTypes = {
  type: PropTypes.oneOf(["tabs", "buttons", "column"]),
  renderTabs: PropTypes.array,
  labelColor: PropTypes.string,
  lineBottomTabs: PropTypes.string,
  selectedColor: PropTypes.string,
  firstTabOpen: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hoverSelectedColor: PropTypes.string,
  onGetTab: PropTypes.func,
  fixedArea: PropTypes.bool,
  railWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  barHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
